import { gsap } from 'gsap';
import { useEffect, useRef } from 'react';
import { useMount } from 'ahooks';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';

let deltaProAnmi: any = null;
let otherAnmi: any = null;
let isFirstIn = true;

export default function MultiCharge({}: any) {
    const { useState, useLang, useRouter } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('delta_pro_new');
    const [activeTab, setActiveTab] = useState({
        key: 0,
        width: locale === 'es' ? 296 : locale === 'cis' ? 374 : 270,
        left: 0,
    });
    const activeRef = useRef();
    const [deltaProMultiChargeProgress, setDeltaProMultiChargeProgress] = useState({
        hrs: 0,
        percentage: 0,
    });
    const [otherMultiChargeProgress, setOtherMultiChargeProgress] = useState({
        hrs: 0,
        percentage: 0,
    });

    const tabs =
        locale === 'ph'
            ? [{ title: t('3000W_multi_charge'), key: 0, hrs: 1.9 }]
            : [
                  { title: t('6500W_multi_charge'), key: 0, hrs: 2.0 },
                  { title: t('5000W_multi_charge'), key: 1, hrs: 2.4 },
              ];

    const boxRef = useRef<HTMLDivElement>(null);

    const handleChangeTab = (e: any, key: any) => {
        if (key === activeTab.key) return;
        const target = e.target;
        changeTab(key, target);
    };

    const changeTab = (key: number, target: any) => {
        const { clientWidth, offsetLeft } = target;
        setActiveTab({ key, width: clientWidth, left: offsetLeft });
        deltaProAnmi.progress(0);
        otherAnmi.progress(0);
        deltaProAnmi.play();
        otherAnmi.play();
    };

    const handleScroll = () => {
        const viewPortHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const container = document.querySelector('.multi_charge_content .content');
        const clientReact = container?.getBoundingClientRect();
        const bottom = clientReact?.bottom || 0;
        if (bottom < viewPortHeight && isFirstIn) {
            isFirstIn = false;
            deltaProAnmi.progress(0);
            otherAnmi.progress(0);
            deltaProAnmi.play();
            otherAnmi.play();
        }
    };

    useEffect(() => {
        deltaProAnmi = gsap.from('.deltaPro .progress_bar', {
            width: 50,
            duration: 1,
            onUpdate() {
                const progress = this.progress();
                const currentTabHrs = tabs.find((tab) => tab.key === activeRef.current)?.hrs || 2;
                let hrs = (progress * currentTabHrs).toFixed(1);
                if (locale === 'de') {
                    hrs = hrs.replace('.', ',');
                }
                const percentage = Math.floor(progress * 100);
                setDeltaProMultiChargeProgress({ hrs, percentage });
            },
        });
        otherAnmi = gsap.from('.other .progress_bar', {
            width: 50,
            duration: 4,
            onUpdate() {
                const progress = this.progress();
                let hrs = (progress * 12).toFixed(1);
                if (locale === 'de') {
                    hrs = hrs.replace('.', ',');
                }
                const percentage = Math.floor(progress * 100);
                setOtherMultiChargeProgress({ hrs, percentage });
            },
        });
        deltaProAnmi.pause();
        otherAnmi.pause();
        deltaProAnmi.progress(0);
        otherAnmi.progress(0);
        setTimeout(handleScroll, 0);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            deltaProAnmi = null;
            otherAnmi = null;
            isFirstIn = true;
        };
    }, []);

    useEffect(() => {
        activeRef.current = activeTab.key;
    }, [activeTab]);

    useMount(() => {
        if (boxRef.current) {
            try {
                changeTab(0, boxRef.current.childNodes[0]);
            } catch (e) {}
        }
    });

    return (
        <div
            className={`${css.multiCharge} multiChargeAnime GtmModuleData-section`}
            data-section-id={css.multiCharge}
            data-section-title={t.rich('multi_charge', {
                green: (child: string) => child,
                normal: (child: string) => child,
            })}
        >
            <div className="title-content">
                <h3 className="title">
                    {t.rich('multi_charge', {
                        green: (child: string) => <p className="line2 font-family-extrabold">{child}</p>,
                        normal: (child: string) => <p className="line3 font-family-bold">{child}</p>,
                    })}
                </h3>
                <p className="desc font-family-semibolder">{t('multi_charge_desc')}</p>
            </div>

            <div className="multi_charge_content">
                <div className="tab_content">
                    <div className="tab" ref={boxRef}>
                        {tabs.map((tab) => (
                            <div
                                key={tab.key}
                                className={`tab_item font-family-bold ${tab.key === activeTab.key ? 'active' : ''}`}
                                onClick={() => handleChangeTab(event, tab.key)}
                            >
                                {tab.title}
                            </div>
                        ))}
                    </div>
                    <div className="line" style={{ width: activeTab.width, left: activeTab.left }} />
                </div>
                <div className="content">
                    <div className="deltaPro">
                        <div className="left">
                            <h6 className="name font-family-semibold">{t('ecoflow_delta_pro')}</h6>
                            <div className="charge font-family-extrabold">
                                {activeTab.key === 0 ? t('smart_home_panel_solar_charging_smart_generator') : t('smart_home_panel_solar_charging')}
                            </div>
                        </div>
                        <div className="right">
                            <div className="progress_bar">
                                <p className="percentage font-family-semibold">{deltaProMultiChargeProgress.percentage}%</p>
                                <div className="time">
                                    <div className="hrs font-family-bold">{deltaProMultiChargeProgress.hrs}</div>
                                    <div className="unit font-family-semibold">{t('Hrs')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="other">
                        <div className="left">
                            <h6 className="name font-family-semibold">{t('other_portable_sower_stations')}</h6>
                            <div className="charge font-family-extrabold">{t('wall_charging')}</div>
                        </div>
                        <div className="right">
                            <div className="progress_bar">
                                <p className="percentage font-family-semibold">{otherMultiChargeProgress.percentage}%</p>
                                <div className="time">
                                    <div className="hrs font-family-bold">{otherMultiChargeProgress.hrs}</div>
                                    <div className="unit font-family-semibold">{t('Hrs')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="note font-family-regular">{t('multi_charge_notice')}</p>
        </div>
    );
}
